import type { CrossSellInput } from 'types';

/**
 * The ordering url s12y builds and delivers on linkout offers, attribution
 * already baked in. Every offer at an address carries the same url in practice,
 * so the first usable one wins. Non-http(s) schemes are rejected rather than
 * handed to window.location.
 */
export function initialLinkoutUrl(
  offers: CrossSellInput['serviceabilityOffers']
): string | undefined {
  return offers
    ?.map((offer) => ('url' in offer ? offer.url : undefined))
    .find((value) => typeof value === 'string' && /^https?:\/\//.test(value));
}

/**
 * Forces query params onto a url, overriding whatever the backend sent. Only
 * params already present are rewritten — nothing is appended — so a url without
 * the param is returned untouched.
 *
 * Splits the query on `&` and compares key names exactly rather than
 * round-tripping through URL/searchParams, which would re-encode the whole
 * query string, turning s12y's `%20` into `+` and betting on how a third-party
 * endpoint parses it. Every other pair is passed through byte-for-byte.
 */
export function applyParamOverrides(
  url: string,
  overrides: Record<string, string>
): string {
  const separator = url.indexOf('?');
  if (separator === -1) return url;

  const [query, ...fragments] = url.slice(separator + 1).split('#');
  const rewritten = query
    .split('&')
    .map((pair) => {
      const equals = pair.indexOf('=');
      const key = equals === -1 ? pair : pair.slice(0, equals);
      return key in overrides ? `${key}=${overrides[key]}` : pair;
    })
    .join('&');

  return [
    url.slice(0, separator),
    '?',
    rewritten,
    ...fragments.flatMap((f) => ['#', f]),
  ].join('');
}

/**
 * The default provider redirect: go wherever s12y says, or nowhere. Returning
 * undefined holds the redirect and leaves the plans page visible rather than
 * stranding the user.
 */
export function providerRedirectUrl(input: CrossSellInput): string | undefined {
  return initialLinkoutUrl(input.serviceabilityOffers);
}

// The number we have always sent in Spectrum's `affpn` param — one value for
// every Spectrum microsite, regardless of each one's fallbackPhoneNumber. s12y
// returns a different number on its prebuilt urls; keeping ours is what
// preserves existing attribution.
const SPECTRUM_AFFPN = '866-282-1155';

/**
 * Spectrum's address-localization url, assembled by hand. s12y now delivers an
 * equivalent url on its linkout offers, so this serves two shrinking purposes:
 * the cross-sell redirect, and the fallback below.
 */
export function spectrumLocalizationUrl(input: CrossSellInput): string {
  const unit = input.address.unit || '';
  return `https://etail-customer.spectrum.com/address/localization?v=BVINE&affpn=${SPECTRUM_AFFPN}&a=${encodeURIComponent(
    input.address.street
  )}&u=${encodeURIComponent(unit)}&z=${encodeURIComponent(
    input.address.postalCode
  )}&TransID=${input.dcFwsTid}`;
}

/**
 * Spectrum's provider redirect. Unlike the default it does two extra things,
 * both specific to this provider:
 *
 * 1. Pins `affpn`, because s12y picks its own phone number for that param.
 * 2. Falls back to the hand-built url, because Spectrum has always redirected
 *    unconditionally — losing that to an empty s12y response would be a
 *    regression rather than graceful degradation.
 *
 * The fallback holds when there is no TransID: that param maps to the Falcon
 * session s12y used to retrieve the offers, and Spectrum's endpoint needs it.
 */
export function spectrumRedirectUrl(input: CrossSellInput): string | undefined {
  const backendUrl = initialLinkoutUrl(input.serviceabilityOffers);
  if (backendUrl) {
    return applyParamOverrides(backendUrl, { affpn: SPECTRUM_AFFPN });
  }

  return input.dcFwsTid ? spectrumLocalizationUrl(input) : undefined;
}
